import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Sparkles, X, Send, Loader2, ArrowUpRight, CalendarDays, Users, User, MapPin, Mic, Square, Volume2, VolumeX } from "lucide-react";
import { api, API, euro, fmtDateTime } from "@/lib/api";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const CourseAdvisor = ({ lang, onBook, variant = "wide" }) => {
  const t = (de, en) => (lang === "de" ? de : en);
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(uid());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [sttBusy, setSttBusy] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [micError, setMicError] = useState("");
  const scrollRef = useRef(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const acRef = useRef(null);
  const vadRef = useRef(null);
  const voiceOnRef = useRef(true);
  useEffect(() => { voiceOnRef.current = voiceOn; }, [voiceOn]);

  const greeting = t(
    "Willkommen bei der Golfcoaching Academy Wendlingen! Ich helfe Dir, den passenden Kurs zu finden. Hast Du schon Golferfahrung oder möchtest Du ganz neu starten?",
    "Welcome to Golfcoaching Academy Wendlingen! I'll help you find the right course. Do you already have golf experience or are you starting fresh?"
  );

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: greeting, recommendations: [] }]);
      if (voiceOnRef.current) speak(greeting);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => { scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }); }, [messages, loading]);

  // Cleanup beim Schließen / Unmount
  const stopAudioPlayback = () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch (_) {} audioRef.current = null; }
    setSpeaking(false);
  };
  const teardownMic = () => {
    if (vadRef.current) { clearInterval(vadRef.current); vadRef.current = null; }
    if (mrRef.current && mrRef.current.state !== "inactive") { try { mrRef.current.stop(); } catch (_) {} }
    mrRef.current = null;
    if (streamRef.current) { streamRef.current.getTracks().forEach((tr) => tr.stop()); streamRef.current = null; }
    if (acRef.current) { try { acRef.current.close(); } catch (_) {} acRef.current = null; }
    setRecording(false);
  };
  useEffect(() => () => { teardownMic(); stopAudioPlayback(); }, []);
  useEffect(() => { if (!open) { teardownMic(); stopAudioPlayback(); } }, [open]); // eslint-disable-line

  const speak = async (text) => {
    if (!text) return;
    stopAudioPlayback();
    try {
      const { data } = await api.post("/advisor/voice/speak", { text, lang });
      if (!data?.audio) return;
      const audio = new Audio(`data:${data.mime || "audio/mp3"};base64,${data.audio}`);
      audioRef.current = audio;
      setSpeaking(true);
      audio.onended = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      await audio.play();
    } catch (_) { setSpeaking(false); }
  };

  const send = async (override, viaVoice = false) => {
    const text = (typeof override === "string" ? override : input).trim();
    if (!text || loading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/advisor", { session_id: sessionId, message: text, history });
      const reply = data.reply || "…";
      setMessages((prev) => [...prev, { role: "assistant", content: reply, recommendations: data.recommendations || [] }]);
      if (viaVoice || voiceOnRef.current) speak(reply);
    } catch (e) {
      setMessages((prev) => [...prev, { role: "assistant", content: t("Entschuldige, der Berater ist gerade nicht erreichbar. Bitte versuche es gleich erneut.", "Sorry, the advisor is unavailable right now. Please try again shortly."), recommendations: [] }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  // ---- Mikrofon-Aufnahme mit automatischer Sprechpausen-Erkennung (VAD) ----
  const finishRecording = async (blob) => {
    setSttBusy(true);
    try {
      const fd = new FormData();
      const ext = (blob.type.includes("ogg") ? "ogg" : blob.type.includes("mp4") ? "mp4" : "webm");
      fd.append("audio", blob, `aufnahme.${ext}`);
      const token = localStorage.getItem("gca_token");
      const res = await fetch(`${API}/advisor/voice/transcribe?lang=${lang}`, {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("stt");
      const data = await res.json();
      const text = (data?.text || "").trim();
      if (text) send(text, true);
      else setMicError(t("Nichts verstanden – bitte nochmal.", "Didn't catch that – please try again."));
    } catch (_) {
      setMicError(t("Spracherkennung nicht verfügbar.", "Speech recognition unavailable."));
    } finally {
      setSttBusy(false);
    }
  };

  const startRecording = async () => {
    setMicError("");
    stopAudioPlayback();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mrRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        teardownMic();
        if (blob.size > 800) finishRecording(blob);
      };
      mr.start(200);
      setRecording(true);

      // VAD: Lautstärke überwachen, bei Stille nach erkannter Sprache automatisch stoppen
      const ac = new (window.AudioContext || window.webkitAudioContext)();
      acRef.current = ac;
      const source = ac.createMediaStreamSource(stream);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const startedAt = Date.now();
      let speechStarted = false;
      let lastLoud = Date.now();
      const SILENCE_MS = 1300;
      const THRESH = 0.018;
      vadRef.current = setInterval(() => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        const now = Date.now();
        if (rms > THRESH) { lastLoud = now; if (now - startedAt > 250) speechStarted = true; }
        // Auto-Stopp bei Sprechpause (nur wenn schon gesprochen wurde)
        if (speechStarted && now - lastLoud > SILENCE_MS) { if (mrRef.current?.state === "recording") mrRef.current.stop(); }
        // Sicherheits-Limit 20s, oder wenn gar nicht gesprochen 6s
        if (now - startedAt > 20000 || (!speechStarted && now - startedAt > 6000)) {
          if (mrRef.current?.state === "recording") mrRef.current.stop();
        }
      }, 150);
    } catch (e) {
      setMicError(t("Mikrofonzugriff verweigert. Bitte im Browser erlauben.", "Microphone access denied. Please allow it in your browser."));
      teardownMic();
    }
  };

  const toggleMic = () => { if (recording) { if (mrRef.current?.state === "recording") mrRef.current.stop(); else teardownMic(); } else startRecording(); };

  const toggleVoice = () => {
    setVoiceOn((v) => {
      if (v) stopAudioPlayback();
      return !v;
    });
  };

  return (
    <>
      {variant === "square" ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="advisor-open-btn"
          aria-label={t("Finde Deinen Kurs mit AI Chat", "Find your course with AI chat")}
          className="group relative flex items-center justify-center sm:flex-col sm:items-start sm:justify-between w-12 h-12 p-0 sm:w-44 sm:h-auto md:w-48 sm:aspect-square sm:p-5 bg-[#ff0033] text-white hover:bg-[#cc0029] transition-colors overflow-hidden"
        >
          <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "radial-gradient(120px 120px at var(--x,50%) 40%, rgba(255,255,255,0.22), transparent)" }} />
          <Sparkles className="shrink-0 w-6 h-6 sm:w-[30px] sm:h-[30px]" />
          <span className="hidden sm:block font-heading uppercase tracking-wide text-base leading-tight text-left mt-2">{t("Finde Deinen Kurs mit AI Chat", "Find your course with AI chat")}</span>
          <ArrowUpRight size={20} className="hidden sm:block self-end shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </button>
      ) : (
      <button
        onClick={() => setOpen(true)}
        data-testid="advisor-open-btn"
        className="group relative inline-flex items-center gap-3 bg-[#ff0033] text-white font-heading uppercase tracking-wider text-sm md:text-base px-6 py-4 hover:bg-[#cc0029] transition-colors overflow-hidden"
      >
        <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "radial-gradient(120px 60px at var(--x,50%) 50%, rgba(255,255,255,0.25), transparent)" }} />
        <Sparkles size={20} className="shrink-0" />
        {t("Finde den richtigen Kurs für Dich mit unserem AI Chat", "Find your perfect course with our AI chat")}
        <ArrowUpRight size={18} className="shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </button>
      )}

      {open && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6" data-testid="advisor-panel">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-lg max-h-[85vh] bg-[#0A0A0A] border border-[#ff0033]/30 flex flex-col shadow-2xl rounded-lg overflow-hidden animate-[popIn_0.25s_ease]">
            <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-[#ff0033]/15 to-transparent">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 grid place-items-center bg-[#ff0033]"><Sparkles size={16} className="text-white" /></span>
                <div>
                  <p className="font-heading uppercase tracking-wide text-white text-sm leading-none">{t("KI-Kursberater", "AI Course Advisor")}</p>
                  <p className="text-[10px] text-neutral-400 font-mono-lab uppercase tracking-widest mt-0.5">Golfcoaching Academy</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleVoice}
                  data-testid="advisor-voice-toggle"
                  title={voiceOn ? t("Sprachausgabe an", "Voice output on") : t("Sprachausgabe aus", "Voice output off")}
                  className={`w-9 h-9 grid place-items-center rounded-md transition-colors ${voiceOn ? "bg-[#ff0033] text-white" : "text-neutral-400 hover:text-white hover:bg-white/5"}`}
                >
                  {speaking ? <Volume2 size={18} className="animate-pulse" /> : voiceOn ? <Volume2 size={18} /> : <VolumeX size={18} />}
                </button>
                <button onClick={() => setOpen(false)} data-testid="advisor-close-btn" className="text-neutral-400 hover:text-white ml-1"><X size={20} /></button>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] ${m.role === "user" ? "" : "w-full"}`}>
                    <div className={`px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#ff0033] text-white" : "bg-[#161616] text-neutral-200 border border-white/10"}`} data-testid={`advisor-msg-${m.role}`}>
                      {m.content}
                    </div>
                    {m.recommendations && m.recommendations.length > 0 && (
                      <div className="mt-3 space-y-3" data-testid="advisor-recommendations">
                        {m.recommendations.map((r, idx) => (
                          <div key={r.session_id} className={`bg-[#0F0F0F] border p-3.5 ${idx === 0 ? "border-[#ff0033]" : "border-white/10"}`} data-testid={`advisor-rec-${r.session_id}`}>
                            {idx === 0 && <span className="inline-block font-mono-lab text-[9px] uppercase tracking-widest text-[#ff0033] border border-[#ff0033]/50 px-1.5 py-0.5 mb-2">{t("Beste Empfehlung", "Best match")}</span>}
                            <p className="font-heading uppercase tracking-tight text-white text-base leading-none">{r.name}</p>
                            <p className="text-neutral-500 text-[10px] font-mono-lab uppercase tracking-widest mt-1">{r.category}</p>
                            <div className="grid grid-cols-2 gap-y-1.5 gap-x-3 mt-3 text-[11px] text-neutral-300">
                              <span className="flex items-center gap-1.5"><CalendarDays size={12} className="text-[#ff0033] shrink-0" /> {fmtDateTime(r.start, lang)}</span>
                              <span className="flex items-center gap-1.5"><User size={12} className="text-[#ff0033] shrink-0" /> {r.coach || "—"}</span>
                              <span className="flex items-center gap-1.5"><Users size={12} className="text-[#ff0033] shrink-0" /> {r.free_spots} {t("frei", "free")}</span>
                              <span className="flex items-center gap-1.5"><MapPin size={12} className="text-[#ff0033] shrink-0" /> {r.location || "—"}</span>
                            </div>
                            <div className="flex items-center justify-between mt-3">
                              <span className="font-heading text-lg text-white">{r.price > 0 ? euro(r.price) : t("auf Anfrage", "on request")}</span>
                              <button
                                onClick={() => { onBook(r.course_id, r.session_id); setOpen(false); }}
                                data-testid={`advisor-book-${r.session_id}`}
                                className="bg-[#ff0033] text-white font-mono-lab text-[10px] uppercase tracking-widest px-3 py-2.5 hover:bg-[#cc0029] transition-colors flex items-center gap-1"
                              >
                                {t("Diesen Termin direkt buchen", "Book this date")} <ArrowUpRight size={13} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start"><div className="bg-[#161616] border border-white/10 px-3.5 py-2.5"><Loader2 size={16} className="animate-spin text-[#ff0033]" /></div></div>
              )}
            </div>

            {(recording || sttBusy) && (
              <div className="px-4 py-2 border-t border-white/10 bg-[#ff0033]/10 flex items-center gap-2" data-testid="advisor-voice-status">
                {sttBusy ? (
                  <><Loader2 size={14} className="animate-spin text-[#ff0033]" /><span className="text-xs text-neutral-300">{t("Wird verarbeitet…", "Processing…")}</span></>
                ) : (
                  <>
                    <span className="flex items-center gap-0.5">
                      {[0, 1, 2, 3].map((n) => (<span key={n} className="w-1 bg-[#ff0033] rounded-full animate-[eq_0.9s_ease-in-out_infinite]" style={{ height: 8 + (n % 3) * 5, animationDelay: `${n * 0.12}s` }} />))}
                    </span>
                    <span className="text-xs text-neutral-300">{t("Ich höre zu … sprich einfach, ich sende automatisch bei einer Pause.", "Listening … just speak, I'll send automatically on a pause.")}</span>
                  </>
                )}
              </div>
            )}
            {micError && <div className="px-4 py-2 text-xs text-[#ff6b6b] border-t border-white/10" data-testid="advisor-mic-error">{micError}</div>}

            <div className="p-3 border-t border-white/10 bg-[#0A0A0A]">
              <div className="flex items-end gap-2">
                <button
                  onClick={toggleMic}
                  disabled={sttBusy || loading}
                  data-testid="advisor-mic-btn"
                  title={recording ? t("Aufnahme stoppen", "Stop recording") : t("Per Sprache antworten", "Answer by voice")}
                  className={`p-3 transition-colors disabled:opacity-40 ${recording ? "bg-[#ff0033] text-white animate-pulse" : "bg-[#161616] border border-white/10 text-neutral-300 hover:text-white hover:border-[#ff0033]"}`}
                >
                  {recording ? <Square size={18} /> : <Mic size={18} />}
                </button>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  rows={1}
                  placeholder={t("Schreib oder sprich Deine Antwort…", "Type or speak your answer…")}
                  data-testid="advisor-input"
                  className="flex-1 resize-none bg-[#161616] border border-white/10 text-white text-sm p-3 focus:border-[#ff0033] outline-none max-h-28"
                />
                <button onClick={() => send()} disabled={loading || !input.trim()} data-testid="advisor-send-btn" className="bg-[#ff0033] text-white p-3 hover:bg-[#cc0029] transition-colors disabled:opacity-40">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>
              <p className="text-[9px] text-neutral-600 mt-2 text-center">{t("Empfehlungen basieren auf aktuell buchbaren Kursen. Keine Datenspeicherung ohne Zustimmung.", "Recommendations are based on currently bookable courses. No data stored without consent.")}</p>
            </div>
          </div>
        </div>,
        document.body
      )}
      <style>{`@keyframes popIn{from{transform:scale(0.96);opacity:0}to{transform:scale(1);opacity:1}}@keyframes eq{0%,100%{transform:scaleY(0.5)}50%{transform:scaleY(1.4)}}`}</style>
    </>
  );
};

export default CourseAdvisor;
